/*
 * QR Code Model 2 encoder (ISO/IEC 18004), byte mode only.
 *
 * Closely follows the design of "QR Code generator library" by Project Nayuki:
 *
 * Copyright (c) Project Nayuki. (MIT License)
 * https://www.nayuki.io/page/qr-code-generator-library
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 * - The above copyright notice and this permission notice shall be included in
 *   all copies or substantial portions of the Software.
 * - The Software is provided "as is", without warranty of any kind, express or
 *   implied, including but not limited to the warranties of merchantability,
 *   fitness for a particular purpose and noninfringement. In no event shall the
 *   authors or copyright holders be liable for any claim, damages or other
 *   liability, whether in an action of contract, tort or otherwise, arising from,
 *   out of or in connection with the Software or the use or other dealings in the
 *   Software.
 */

export type QrEcc = 'L' | 'M' | 'Q' | 'H';

export interface QrMatrix {
  size: number;
  /** `modules[y][x]`; `true` = koyu modül. */
  modules: boolean[][];
}

const ECC_ORDINAL: Record<QrEcc, number> = { L: 0, M: 1, Q: 2, H: 3 };
const ECC_FORMAT: Record<QrEcc, number> = { L: 1, M: 0, Q: 3, H: 2 };

export const QR_MIN_VERSION = 1;
export const QR_MAX_VERSION = 40;

// Indexed by [ecc ordinal][version]; index 0 is unused padding.
// prettier-ignore
const ECC_CODEWORDS_PER_BLOCK: readonly (readonly number[])[] = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // L
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28], // M
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // Q
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30], // H
];

// prettier-ignore
const NUM_ERROR_CORRECTION_BLOCKS: readonly (readonly number[])[] = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25], // L
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49], // M
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68], // Q
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81], // H
];

const PENALTY_N1 = 3;
const PENALTY_N2 = 3;
const PENALTY_N3 = 40;
const PENALTY_N4 = 10;

const at = <T>(arr: readonly T[], i: number): T => arr[i] as T;
const getBit = (x: number, i: number): boolean => ((x >>> i) & 1) !== 0;

/* ---------- Reed-Solomon over GF(2^8), primitive polynomial 0x11D ---------- */

export function gfMultiply(x: number, y: number): number {
  let z = 0;
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> i) & 1) * x;
  }
  return z & 0xff;
}

/** Generator polynomial coefficients of the given degree, highest power first, leading 1 omitted. */
function rsDivisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < result.length; j++) {
      const next = j + 1 < result.length ? at(result, j + 1) : 0;
      result[j] = gfMultiply(at(result, j), root) ^ next;
    }
    root = gfMultiply(root, 0x02);
  }
  return result;
}

function rsRemainder(data: readonly number[], divisor: readonly number[]): number[] {
  const result = new Array<number>(divisor.length).fill(0);
  for (const b of data) {
    const factor = b ^ (result.shift() as number);
    result.push(0);
    divisor.forEach((coef, i) => {
      result[i] = at(result, i) ^ gfMultiply(coef, factor);
    });
  }
  return result;
}

/** Error-correction codewords for one block. */
export function reedSolomonEcc(data: readonly number[], degree: number): number[] {
  return rsRemainder(data, rsDivisor(degree));
}

/* ---------- Capacity tables ---------- */

/** Number of data+EC bits available in a symbol after all function patterns are excluded. */
export function numRawDataModules(ver: number): number {
  let result = (16 * ver + 128) * ver + 64;
  if (ver >= 2) {
    const numAlign = Math.floor(ver / 7) + 2;
    result -= (25 * numAlign - 10) * numAlign - 55;
    if (ver >= 7) result -= 36;
  }
  return result;
}

export function numDataCodewords(ver: number, ecc: QrEcc): number {
  const e = ECC_ORDINAL[ecc];
  return (
    Math.floor(numRawDataModules(ver) / 8) -
    at(at(ECC_CODEWORDS_PER_BLOCK, e), ver) * at(at(NUM_ERROR_CORRECTION_BLOCKS, e), ver)
  );
}

const charCountBits = (ver: number): number => (ver <= 9 ? 8 : 16);

/** Maximum payload in bytes (byte mode) for a version/ECC level. */
export function byteCapacity(ver: number, ecc: QrEcc): number {
  return Math.floor((numDataCodewords(ver, ecc) * 8 - 4 - charCountBits(ver)) / 8);
}

/** Smallest version that fits `byteLength` bytes in byte mode, or `-1`. */
export function selectVersion(byteLength: number, ecc: QrEcc): number {
  for (let ver = QR_MIN_VERSION; ver <= QR_MAX_VERSION; ver++) {
    if (byteLength <= byteCapacity(ver, ecc)) return ver;
  }
  return -1;
}

/* ---------- Format / version information ---------- */

/** 15-bit format information (BCH(15,5) + mask 0x5412). */
export function formatBits(ecc: QrEcc, mask: number): number {
  const data = (ECC_FORMAT[ecc] << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  return ((data << 10) | rem) ^ 0x5412;
}

/** 18-bit version information (BCH(18,6)) for versions >= 7. */
export function versionBits(ver: number): number {
  let rem = ver;
  for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
  return (ver << 12) | rem;
}

export function alignmentPatternPositions(ver: number): number[] {
  if (ver === 1) return [];
  const numAlign = Math.floor(ver / 7) + 2;
  const step = Math.floor((ver * 8 + numAlign * 3 + 5) / (numAlign * 4 - 4)) * 2;
  const result = [6];
  for (let pos = ver * 4 + 17 - 7; result.length < numAlign; pos -= step) result.splice(1, 0, pos);
  return result;
}

/* ---------- Data encoding ---------- */

function addEccAndInterleave(data: readonly number[], ver: number, ecc: QrEcc): number[] {
  const e = ECC_ORDINAL[ecc];
  const numBlocks = at(at(NUM_ERROR_CORRECTION_BLOCKS, e), ver);
  const blockEccLen = at(at(ECC_CODEWORDS_PER_BLOCK, e), ver);
  const rawCodewords = Math.floor(numRawDataModules(ver) / 8);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLen = Math.floor(rawCodewords / numBlocks);

  const divisor = rsDivisor(blockEccLen);
  const blocks: number[][] = [];
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const dat = data.slice(k, k + shortBlockLen - blockEccLen + (i < numShortBlocks ? 0 : 1));
    k += dat.length;
    const eccWords = rsRemainder(dat, divisor);
    if (i < numShortBlocks) dat.push(0);
    blocks.push(dat.concat(eccWords));
  }

  const result: number[] = [];
  const blockLen = at(blocks, 0).length;
  for (let i = 0; i < blockLen; i++) {
    blocks.forEach((block, j) => {
      if (i !== shortBlockLen - blockEccLen || j >= numShortBlocks) result.push(at(block, i));
    });
  }
  return result;
}

function encodeDataCodewords(bytes: Uint8Array, ver: number, ecc: QrEcc): number[] {
  const bits: number[] = [];
  const append = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1);
  };
  append(0b0100, 4);
  append(bytes.length, charCountBits(ver));
  for (const b of bytes) append(b, 8);

  const capacityBits = numDataCodewords(ver, ecc) * 8;
  append(0, Math.min(4, capacityBits - bits.length));
  append(0, (8 - (bits.length % 8)) % 8);
  for (let pad = 0xec; bits.length < capacityBits; pad ^= 0xec ^ 0x11) append(pad, 8);

  const codewords = new Array<number>(bits.length / 8).fill(0);
  bits.forEach((b, i) => {
    codewords[i >>> 3] = at(codewords, i >>> 3) | (b << (7 - (i & 7)));
  });
  return codewords;
}

/* ---------- Matrix construction ---------- */

class QrSymbol {
  readonly version: number;
  readonly ecc: QrEcc;
  readonly size: number;
  readonly modules: boolean[][];
  private readonly isFunction: boolean[][];

  constructor(version: number, ecc: QrEcc) {
    this.version = version;
    this.ecc = ecc;
    this.size = version * 4 + 17;
    this.modules = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
    this.isFunction = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false));
  }

  get(x: number, y: number): boolean {
    return at(at(this.modules, y), x);
  }

  private set(x: number, y: number, dark: boolean): void {
    at(this.modules, y)[x] = dark;
  }

  private setFunction(x: number, y: number, dark: boolean): void {
    this.set(x, y, dark);
    at(this.isFunction, y)[x] = true;
  }

  drawFunctionPatterns(): void {
    const { size } = this;
    for (let i = 0; i < size; i++) {
      this.setFunction(6, i, i % 2 === 0);
      this.setFunction(i, 6, i % 2 === 0);
    }
    this.drawFinder(3, 3);
    this.drawFinder(size - 4, 3);
    this.drawFinder(3, size - 4);

    const pos = alignmentPatternPositions(this.version);
    const last = pos.length - 1;
    pos.forEach((py, i) => {
      pos.forEach((px, j) => {
        if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) return;
        this.drawAlignment(px, py);
      });
    });

    this.drawFormatBits(0);
    this.drawVersion();
  }

  private drawFinder(x: number, y: number): void {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        const xx = x + dx;
        const yy = y + dy;
        if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) this.setFunction(xx, yy, dist !== 2 && dist !== 4);
      }
    }
  }

  private drawAlignment(x: number, y: number): void {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) this.setFunction(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }

  drawFormatBits(mask: number): void {
    const bits = formatBits(this.ecc, mask);
    const { size } = this;
    for (let i = 0; i <= 5; i++) this.setFunction(8, i, getBit(bits, i));
    this.setFunction(8, 7, getBit(bits, 6));
    this.setFunction(8, 8, getBit(bits, 7));
    this.setFunction(7, 8, getBit(bits, 8));
    for (let i = 9; i < 15; i++) this.setFunction(14 - i, 8, getBit(bits, i));

    for (let i = 0; i < 8; i++) this.setFunction(size - 1 - i, 8, getBit(bits, i));
    for (let i = 8; i < 15; i++) this.setFunction(8, size - 15 + i, getBit(bits, i));
    this.setFunction(8, size - 8, true);
  }

  private drawVersion(): void {
    if (this.version < 7) return;
    const bits = versionBits(this.version);
    for (let i = 0; i < 18; i++) {
      const dark = getBit(bits, i);
      const a = this.size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      this.setFunction(a, b, dark);
      this.setFunction(b, a, dark);
    }
  }

  drawCodewords(data: readonly number[]): void {
    const { size } = this;
    let i = 0;
    for (let right = size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5;
      for (let vert = 0; vert < size; vert++) {
        for (let j = 0; j < 2; j++) {
          const x = right - j;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? size - 1 - vert : vert;
          if (!at(at(this.isFunction, y), x) && i < data.length * 8) {
            this.set(x, y, getBit(at(data, i >>> 3), 7 - (i & 7)));
            i++;
          }
        }
      }
    }
  }

  applyMask(mask: number): void {
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        if (at(at(this.isFunction, y), x)) continue;
        let invert: boolean;
        switch (mask) {
          case 0: invert = (x + y) % 2 === 0; break;
          case 1: invert = y % 2 === 0; break;
          case 2: invert = x % 3 === 0; break;
          case 3: invert = (x + y) % 3 === 0; break;
          case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break;
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break;
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          case 7: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break;
          default: throw new RangeError(`Geçersiz maske: ${mask}`);
        }
        if (invert) this.set(x, y, !this.get(x, y));
      }
    }
  }

  penaltyScore(): number {
    const { size } = this;
    let result = 0;

    for (const vertical of [false, true]) {
      for (let a = 0; a < size; a++) {
        let runColor = false;
        let runLen = 0;
        const history = [0, 0, 0, 0, 0, 0, 0];
        for (let b = 0; b < size; b++) {
          const dark = vertical ? this.get(a, b) : this.get(b, a);
          if (dark === runColor) {
            runLen++;
            if (runLen === 5) result += PENALTY_N1;
            else if (runLen > 5) result++;
          } else {
            this.finderPenaltyAddHistory(runLen, history);
            if (!runColor) result += this.finderPenaltyCountPatterns(history) * PENALTY_N3;
            runColor = dark;
            runLen = 1;
          }
        }
        result += this.finderPenaltyTerminateAndCount(runColor, runLen, history) * PENALTY_N3;
      }
    }

    for (let y = 0; y < size - 1; y++) {
      for (let x = 0; x < size - 1; x++) {
        const c = this.get(x, y);
        if (c === this.get(x + 1, y) && c === this.get(x, y + 1) && c === this.get(x + 1, y + 1)) result += PENALTY_N2;
      }
    }

    let dark = 0;
    for (const row of this.modules) for (const m of row) if (m) dark++;
    const total = size * size;
    const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1;
    result += k * PENALTY_N4;
    return result;
  }

  private finderPenaltyCountPatterns(h: readonly number[]): number {
    const n = at(h, 1);
    const core = n > 0 && at(h, 2) === n && at(h, 3) === n * 3 && at(h, 4) === n && at(h, 5) === n;
    return (core && at(h, 0) >= n * 4 && at(h, 6) >= n ? 1 : 0) + (core && at(h, 6) >= n * 4 && at(h, 0) >= n ? 1 : 0);
  }

  private finderPenaltyTerminateAndCount(runColor: boolean, runLen: number, h: number[]): number {
    let len = runLen;
    if (runColor) {
      this.finderPenaltyAddHistory(len, h);
      len = 0;
    }
    len += this.size;
    this.finderPenaltyAddHistory(len, h);
    return this.finderPenaltyCountPatterns(h);
  }

  private finderPenaltyAddHistory(runLen: number, h: number[]): void {
    h.pop();
    h.unshift(at(h, 0) === 0 ? runLen + this.size : runLen);
  }
}

/**
 * Metni (UTF-8, byte modu) QR koduna çevirir; en küçük uygun sürüm seçilir ve
 * 8 maskeden ceza puanı en düşük olan uygulanır.
 */
export function encodeQr(text: string, ecc: QrEcc = 'M'): QrMatrix {
  const bytes = new TextEncoder().encode(text);
  const version = selectVersion(bytes.length, ecc);
  if (version < 0) throw new RangeError(`QR kodu için metin çok uzun (${bytes.length} bayt)`);

  const sym = new QrSymbol(version, ecc);
  sym.drawFunctionPatterns();
  sym.drawCodewords(addEccAndInterleave(encodeDataCodewords(bytes, version, ecc), version, ecc));

  let bestMask = 0;
  let minPenalty = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    sym.applyMask(mask);
    sym.drawFormatBits(mask);
    const penalty = sym.penaltyScore();
    if (penalty < minPenalty) {
      bestMask = mask;
      minPenalty = penalty;
    }
    sym.applyMask(mask);
  }
  sym.applyMask(bestMask);
  sym.drawFormatBits(bestMask);

  return { size: sym.size, modules: sym.modules.map((row) => row.slice()) };
}
